import { MoreHorizontal } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import OptionButton from '@/components-latest/primitives/OptionButton'
import {
  PopoverContent,
  PopoverTrigger
} from '@/components-latest/primitives/Popover'
import Popover from '@/components-latest/primitives/Popover/Popover'
import Text from '@/components-latest/primitives/Text'
import DisableConfirmation from '@/components-latest/ui/OfferPage/DisableConfirmation'
import useCampaignActions from '@/components-latest/ui/CampaignsPage/useCampaignActions'
import type { Campaign } from '@/ducks/campaigns/campaigns.types'
import type { FetchCampaignsQueryParams } from '@/ducks/campaigns/api/campaigns.api'
import { useToggle } from '@/hooks/useToggle/useToggle'

import Styles from './CampaignsTable.module.scss'

type Props = {
  campaign: Campaign
  campaignsQueryParams?: FetchCampaignsQueryParams
}

const CampaignActionsMenu = ({ campaign, campaignsQueryParams }: Props) => {
  const { t } = useTranslation()
  const [isOpen, toggleIsOpen] = useToggle(false)

  const {
    handleEdit,
    handleDeactivate,
    handleDelete,
    isExpired,
    isDeactivateDisabled,
    deactivateLabel,
    deactivateDescription,
    deleteTitle,
    deleteDescription,
    isDeleteOpen,
    setIsDeleteOpen,
    isDeactivateOpen,
    setIsDeactivateOpen
  } = useCampaignActions(campaign, { campaignsQueryParams })

  const closeMenu = () => {
    if (isOpen) toggleIsOpen()
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={toggleIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={Styles.iconButton}
            aria-label={t('marketing.campaigns.more-actions')}
            data-testid={`campaign-actions-${campaign.campaignId}`}>
            <MoreHorizontal width={16} height={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className={Styles.actionsPopover}>
          <OptionButton
            className={Styles.actionOption}
            disabled={isExpired}
            onClick={() => {
              closeMenu()
              handleEdit()
            }}>
            <Text variant="nextSubtitle">{t('offers.edit-offer')}</Text>
          </OptionButton>
          <OptionButton
            className={Styles.actionOption}
            disabled={isDeactivateDisabled}
            onClick={() => {
              closeMenu()
              setIsDeactivateOpen(true)
            }}>
            <Text variant="nextSubtitle">{deactivateLabel}</Text>
          </OptionButton>
          <OptionButton
            className={Styles.actionOption}
            onClick={() => {
              closeMenu()
              setIsDeleteOpen(true)
            }}>
            <Text variant="nextSubtitle">{t('offers.delete-offer')}</Text>
          </OptionButton>
        </PopoverContent>
      </Popover>

      <DisableConfirmation
        open={isDeactivateOpen}
        onOpenChange={setIsDeactivateOpen}
        onConfirm={handleDeactivate}
        title={deactivateLabel}
        description={deactivateDescription}
        cancelText={t('common.modal.cancel')}
        confirmText={t('common.modal.confirm')}
      />

      <DisableConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDelete}
        title={deleteTitle}
        description={deleteDescription}
        cancelText={t('common.modal.cancel')}
        confirmText={t('common.modal.confirm')}
      />
    </>
  )
}

export default CampaignActionsMenu
